import { get, post } from '@/shared/utils/FetchV2.util';
import type {
	PlanChangeChannel,
	PlanChangeTiming,
	PlanChangeRecord,
	PlanChangeResult,
} from '../lib/types';

/**
 * The customer routes of the plan change in heru-sales-transaction-engine. The
 * user comes from the JWT. `skipAccountError` keeps every 4xx as a thrown error
 * (FetchV2 would hand a 409 back as data): the refusals are the flow's answers.
 */

export const quotePlanChange = (
	subscriptionId: number,
	toProductVersionId: number,
) =>
	post<PlanChangeResult>({
		service: 'salesTransactionEngine',
		endpoint: `/subscriptions/${subscriptionId}/plan-change/quote`,
		body: { to_product_version_id: toProductVersionId },
		skipAccountError: true,
	});

/**
 * `expectedDiffCents` is the quote the customer saw: if the money moved, the
 * engine answers 409 and changes nothing. `timing: 'renewal'` schedules a
 * downgrade for the renewal without heru-cash (a different quote, so it is not
 * checked against the one on screen).
 */
export const requestPlanChange = ({
	subscriptionId,
	toProductVersionId,
	channel,
	expectedDiffCents,
	timing = 'now',
}: {
	subscriptionId: number;
	toProductVersionId: number;
	channel: PlanChangeChannel;
	expectedDiffCents: number;
	timing?: PlanChangeTiming;
}) =>
	post<PlanChangeResult>({
		service: 'salesTransactionEngine',
		endpoint: `/subscriptions/${subscriptionId}/plan-change`,
		body: {
			to_product_version_id: toProductVersionId,
			channel,
			...(timing === 'renewal' ? { timing } : { expected_diff_cents: expectedDiffCents }),
		},
		skipAccountError: true,
	});

/** Answers 503 while the plan change is not open to this user: that is how the web knows to offer it. */
export const listPlanChanges = async (subscriptionId: number): Promise<PlanChangeRecord[]> => {
	const response = await get<{ plan_changes: PlanChangeRecord[] }>({
		service: 'salesTransactionEngine',
		endpoint: `/subscriptions/${subscriptionId}/plan-changes`,
		skipAccountError: true,
	});
	return response?.plan_changes ?? [];
};
